import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import CustomerSearchField from "@/components/CustomerSearchField";
import FileUploadField from "@/components/FileUploadField";
import FinishedProductPicker from "@/components/FinishedProductPicker";
import BackButton from "@/components/BackButton";
import ShareButton from "@/components/ShareButton";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Plus, Truck, Trash2, FileText, FileDown } from "lucide-react";

const STATUS_COLORS = {
  LOADING: "bg-amber-100 text-amber-900",
  DISPATCHED: "bg-blue-100 text-blue-900",
  DELIVERED: "bg-emerald-100 text-emerald-900",
};

const BLANK = () => ({
  customer_name: "", customer_email: "", distributor_name: "",
  invoice_number: "", eway_bill_no: "", challan_number: "",
  received_challan_url: "",
  vehicle_no: "", driver_name: "",
  gst_percent: 18, tds_percent: 0, freight: 0,
  sales_order_id: "", sales_order_number: "", notes: "",
  status: "DISPATCHED",
  lines: [emptyLine()],
});
function emptyLine() { return { product_id: "", product_code: "", product_name: "", size: "", hsn_code: "", quantity: 1, unit: "box", unit_price: 0 }; }

export default function OrgDispatch() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK());

  const buildShareUrl = async (d, kind) => {
    try {
      const { data } = await api.post("/org/documents/share-link", {
        doc_type: "dispatch", doc_id: d.id, kind,
      });
      const shortUrl = `${window.location.origin}${data.short_path}`;
      return { shortUrl, details: data.details };
    } catch (e) { toast.error(e.response?.data?.detail || "Share link failed"); return null; }
  };

  const load = async () => {
    const [d, so] = await Promise.all([
      api.get("/org/dispatches"),
      api.get("/manufacturing/sales-orders-queue").catch(() => ({ data: [] })),
    ]);
    setRows(d.data);
    setSalesOrders(so.data);
  };
  useEffect(() => { load(); }, []);

  const updateLine = (idx, patch) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], ...patch };
    setForm({ ...form, lines });
  };
  const removeLine = (idx) => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });

  // Live totals
  const subtotal = form.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const gstAmount = subtotal * Number(form.gst_percent || 0) / 100;
  const tdsAmount = subtotal * Number(form.tds_percent || 0) / 100;
  const freight = Number(form.freight || 0);
  const total = subtotal + gstAmount - tdsAmount + freight;

  const submit = async () => {
    if (!form.customer_name) { toast.error("Customer is required"); return; }
    if (!form.lines.length || !form.lines.some((l) => l.product_name && Number(l.quantity) > 0)) {
      toast.error("Add at least one product line"); return;
    }
    try {
      await api.post("/org/dispatches", {
        ...form,
        lines: form.lines
          .filter((l) => l.product_name && Number(l.quantity) > 0)
          .map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price || 0) })),
        gst_percent: Number(form.gst_percent || 0),
        tds_percent: Number(form.tds_percent || 0),
        freight: Number(form.freight || 0),
      });
      toast.success("Dispatch created — invoice/e-way/challan generated");
      setOpen(false); setForm(BLANK()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (d, status) => {
    try { await api.patch(`/org/dispatches/${d.id}`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = (dsp, kind) => {
    const token = (typeof sessionStorage !== "undefined" && sessionStorage.getItem("cera_token")) || localStorage.getItem("cera_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/org/dispatches/${dsp.id}/pdf/${kind}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${kind}-${dsp.number}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Could not download PDF"));
  };

  const pickSalesOrder = (soId) => {
    const so = salesOrders.find((s) => s.id === soId);
    if (!so) return;
    setForm({
      ...form,
      sales_order_id: so.id,
      sales_order_number: so.number,
      customer_name: so.customer_name || form.customer_name,
      lines: so.product_name ? [{
        ...emptyLine(),
        product_name: so.product_name,
        product_code: so.product_code || "",
        quantity: so.total_qty || 1,
        unit_price: (so.total_qty ? (so.amount || 0) / so.total_qty : 0),
      }] : form.lines,
    });
    toast.success(`Loaded from ${so.number}`);
  };

  const totalSqm = rows.reduce((s, r) => s + Number(r.total_sqm || 0), 0);
  const totalValue = rows.reduce((s, r) => s + Number(r.total_value || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="org-dispatch-page">
      <BackButton />
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-3"><Truck className="w-7 h-7" /> Dispatch</h1>
          <p className="text-muted-foreground">Outbound goods with automatic Invoice, E-way bill and Challan generation.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(BLANK()); }}>
          <DialogTrigger asChild><Button data-testid="new-dispatch-btn"><Plus className="w-4 h-4 mr-1" /> New dispatch</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New dispatch</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <CustomerSearchField
                testid="dispatch-customer-search" label="Customer" placeholder="Search existing customers…"
                value={form.customer_email ? { name: form.customer_name, email: form.customer_email } : null}
                onChange={(opt) => setForm({ ...form, customer_name: opt.name || opt.email || "", customer_email: opt.email || "" })}
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Sales order (optional)</Label>
                  <Select value={form.sales_order_id || ""} onValueChange={pickSalesOrder}>
                    <SelectTrigger data-testid="dsp-so-select"><SelectValue placeholder={salesOrders.length ? "Pick sales order to prefill" : "No sales orders yet"} /></SelectTrigger>
                    <SelectContent>
                      {salesOrders.map((s) => <SelectItem key={s.id} value={s.id}>{s.number} · {s.customer_name} · {s.product_name || "—"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Distributor (optional)</Label>
                  <Input value={form.distributor_name} onChange={(e) => setForm({ ...form, distributor_name: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Invoice # (auto)</Label><Input value={form.invoice_number} placeholder="Auto-generated" onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="dsp-invoice-no" /></div>
                <div className="space-y-2"><Label>E-way bill # (auto)</Label><Input value={form.eway_bill_no} placeholder="Auto-generated" onChange={(e) => setForm({ ...form, eway_bill_no: e.target.value })} data-testid="dsp-eway-no" /></div>
                <div className="space-y-2"><Label>Challan # (auto)</Label><Input value={form.challan_number} placeholder="Auto-generated" onChange={(e) => setForm({ ...form, challan_number: e.target.value })} data-testid="dsp-challan-no" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Vehicle no.</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} data-testid="dsp-vehicle" /></div>
                <div className="space-y-2"><Label>Driver</Label><Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} /></div>
              </div>

              {/* Product lines */}
              <div className="border border-border rounded-md" data-testid="dsp-lines">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
                  <div className="label-overline">Product lines</div>
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })} data-testid="dsp-add-line">+ Add line</Button>
                </div>
                <div className="p-3 space-y-3">
                  {form.lines.map((l, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`dsp-line-${idx}`}>
                      <div className="col-span-5">
                        <Label className="text-xs">Product</Label>
                        <FinishedProductPicker
                          value={l.product_id}
                          onPick={(p) => updateLine(idx, { product_id: p.id, product_code: p.code, product_name: p.name, size: p.size, hsn_code: p.hsn_code, unit: p.unit || "box", unit_price: p.sale_price || 0 })}
                          testId={`dsp-line-${idx}-picker`}
                          placeholder="Pick finished product…"
                          fallbackLabel={l.product_name ? `${l.product_code || ""} · ${l.product_name}` : null}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Qty</Label>
                        <Input type="number" value={l.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} data-testid={`dsp-line-${idx}-qty`} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Unit</Label>
                        <Input value={l.unit} onChange={(e) => updateLine(idx, { unit: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Rate ₹</Label>
                        <Input type="number" value={l.unit_price} onChange={(e) => updateLine(idx, { unit_price: e.target.value })} data-testid={`dsp-line-${idx}-price`} />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        {form.lines.length > 1 && <button onClick={() => removeLine(idx)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* GST + TDS + totals */}
              <div className="grid grid-cols-3 gap-3">
                <div><Label>GST %</Label><Input type="number" step="0.01" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} data-testid="dsp-gst-pct" /></div>
                <div><Label>TDS %</Label><Input type="number" step="0.01" value={form.tds_percent} onChange={(e) => setForm({ ...form, tds_percent: e.target.value })} data-testid="dsp-tds-pct" /></div>
                <div><Label>Freight ₹</Label><Input type="number" step="0.01" value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} data-testid="dsp-freight" /></div>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 space-y-1 text-sm" data-testid="dsp-totals">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">₹{subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">GST ({form.gst_percent || 0}%)</span><span className="font-mono">+₹{gstAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
                {Number(form.tds_percent) > 0 ? <div className="flex justify-between text-emerald-700"><span>TDS ({form.tds_percent}%)</span><span className="font-mono">-₹{tdsAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div> : null}
                {freight > 0 ? <div className="flex justify-between"><span className="text-muted-foreground">Freight</span><span className="font-mono">+₹{freight.toLocaleString()}</span></div> : null}
                <div className="flex justify-between border-t border-border pt-1 font-bold"><span>Total</span><span className="font-mono text-lg">₹{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
              </div>

              <div className="space-y-2"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["LOADING", "DISPATCHED", "DELIVERED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <FileUploadField label="Signed challan (optional)" value={form.received_challan_url} onChange={(v) => setForm({ ...form, received_challan_url: v })} accept="application/pdf,image/png,image/jpeg" testid="dsp-challan-upload" />
              <Button onClick={submit} className="w-full" data-testid="dsp-save" disabled={!form.customer_name}>Create dispatch &amp; generate documents</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="surface-card p-5"><div className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-600">Total dispatches</div><div className="font-display text-2xl sm:text-3xl font-black mt-2">{rows.length}</div></div>
        <div className="surface-card p-5"><div className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-600">Total SQM</div><div className="font-display text-2xl sm:text-3xl font-black mt-2">{totalSqm.toLocaleString()}</div></div>
        <div className="surface-card p-5"><div className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-600">Total value</div><div className="font-display text-2xl sm:text-3xl font-black mt-2">₹{totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>DSP #</TableHead><TableHead>Invoice</TableHead><TableHead>E-way</TableHead><TableHead>Challan</TableHead>
            <TableHead>Customer</TableHead><TableHead>Vehicle</TableHead><TableHead>GST</TableHead><TableHead>Total</TableHead>
            <TableHead>Documents</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.id} data-testid={`dispatch-row-${d.id}`}>
                <TableCell className="font-mono text-xs">{d.number}</TableCell>
                <TableCell className="font-mono text-xs">{d.invoice_number || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{d.eway_bill_no || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{d.challan_number || "—"}</TableCell>
                <TableCell className="text-sm">{d.customer_name}</TableCell>
                <TableCell className="font-mono text-xs">{d.vehicle_no || "—"}</TableCell>
                <TableCell className="font-mono text-xs">₹{Number(d.gst_amount || 0).toLocaleString()}</TableCell>
                <TableCell className="font-mono font-bold">₹{Number(d.total_value || 0).toLocaleString()}</TableCell>
                <TableCell>
                  <div className="flex gap-1 items-center">
                    <button onClick={() => downloadPdf(d, "invoice")} title="Invoice PDF" className="p-1.5 rounded hover:bg-accent" data-testid={`dsp-pdf-invoice-${d.id}`}><FileText className="w-4 h-4" /></button>
                    <button onClick={() => downloadPdf(d, "eway")} title="E-way bill PDF" className="p-1.5 rounded hover:bg-accent" data-testid={`dsp-pdf-eway-${d.id}`}><FileDown className="w-4 h-4" /></button>
                    <button onClick={() => downloadPdf(d, "challan")} title="Challan PDF" className="p-1.5 rounded hover:bg-accent" data-testid={`dsp-pdf-challan-${d.id}`}><FileDown className="w-4 h-4 text-emerald-600" /></button>
                    <ShareButton
                      docLabel={`${t("common.invoice")} ${d.invoice_number || d.number}`}
                      resolveShare={() => buildShareUrl(d, "invoice")}
                      testId={`dsp-share-${d.id}`}
                      variant="ghost"
                    />
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline" className={`font-mono text-[10px] ${STATUS_COLORS[d.status] || ""}`}>{d.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Select value={d.status} onValueChange={(v) => updateStatus(d, v)}>
                    <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{["LOADING", "DISPATCHED", "DELIVERED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No dispatches yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
